/**
 * Formatting helpers. No DOM, no React, no imports from anything platform-specific.
 *
 * The `core/` directory is deliberately framework-agnostic. That discipline costs a
 * few days now and preserves the option of retargeting the UI to Expo later: the
 * business logic ports unchanged and only the presentation layer is rewritten.
 */

/** Chinese market convention: red is up, green is down. */
export type Direction = "up" | "down" | "flat";

export function direction(value: number | null | undefined): Direction {
  if (value === null || value === undefined || Number.isNaN(value)) return "flat";
  if (value > 0) return "up";
  if (value < 0) return "down";
  return "flat";
}

export function formatPrice(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "--";
  return value.toFixed(digits);
}

export function formatPercent(
  value: number | null | undefined,
  { digits = 2, sign = true }: { digits?: number; sign?: boolean } = {},
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "--";
  const scaled = value * 100;
  const prefix = sign && scaled > 0 ? "+" : "";
  return `${prefix}${scaled.toFixed(digits)}%`;
}

export function formatSigned(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "--";
  return `${value > 0 ? "+" : ""}${value.toFixed(digits)}`;
}

/**
 * Chinese magnitude units. A market cap of 1.2e11 reads as 1200亿 to a domestic user
 * and as an unparseable string of digits otherwise.
 */
export function formatMagnitude(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "--";
  const magnitude = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (magnitude >= 1e12) return `${sign}${(magnitude / 1e12).toFixed(digits)}万亿`;
  if (magnitude >= 1e8) return `${sign}${(magnitude / 1e8).toFixed(digits)}亿`;
  if (magnitude >= 1e4) return `${sign}${(magnitude / 1e4).toFixed(digits)}万`;
  return `${sign}${magnitude.toFixed(digits)}`;
}

/** Volume is stored in shares; 手 is what a user reads. */
export function formatVolume(shares: number | null | undefined): string {
  if (shares === null || shares === undefined || Number.isNaN(shares)) return "--";
  return formatMagnitude(shares / 100, 0);
}

export function formatBasisPoints(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "--";
  return `${value.toFixed(0)}bp`;
}

export function formatRatio(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "--";
  if (!Number.isFinite(value)) return "∞";
  return value.toFixed(digits);
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return "--";
  return value.slice(0, 10);
}

export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined) return "--";
  return value.toLocaleString("zh-CN");
}

/**
 * The label an owner sees beside an invite code.
 *
 * "Used" is checked before "usable" on purpose: a redeemed code is also unusable, and
 * reporting it as expired would make the owner think a person never got in when they
 * did. The server's `usable` already folds both conditions together, so the order here
 * is what separates them.
 */
export function inviteStatus(invite: {
  usable: boolean;
  used_by: number | null;
  expires_at: string | null;
}): string {
  if (invite.used_by !== null) return "已使用";
  if (!invite.usable) return "已过期";
  return invite.expires_at ? `有效至 ${formatDate(invite.expires_at)}` : "有效";
}

/** Compact date for a chart axis, which has no room for the year on a phone. */
export function formatAxisDate(value: string): string {
  const parts = value.slice(0, 10).split("-");
  return parts.length === 3 ? `${parts[1]}/${parts[2]}` : value;
}

export function classNames(...values: (string | false | null | undefined)[]): string {
  return values.filter(Boolean).join(" ");
}
